import { ListView, ListViewColumnType, FilterType, initListView } from '../../UI/view/list-view';
import { placedOrdersDS, ordersPermissions } from './orders-datasource';
import { OrderType, isDewarOnlyFilter, ordersListDebounce } from './orders-types';
import { getSourcePlantOptions } from '../../../services/requests/orders/orders-requests';

// Order Number	Booking ID	Customer Name	Port Cutoff	Date Requested	City	State	Container #	Location	Source Plant	Delivery Type	PO #/Ord ID	MRI/NMR	P/U Only	Order Type
export const currentOrdersListView = {
  permissions: ordersPermissions,
  addButtonText: 'Create Order',
  addButtonPath: '/orders/create',
  filterGroup: 'orders',
  ...ordersListDebounce,
  filters: [
    {
      fieldName: 'orderTypeId',
      type: FilterType.multiselect,
      filterValue: { [1]: 1, [2]: 2, [3]: 3 },
      options: () => [1, 2, 3],
      labels: { 1: 'Gas', 2: 'Liquid', 3: 'Dewar' },
    },
    {
      fieldName: 'shippingLocationName',
      type: FilterType.combo,
      label: 'Source Plant',
      searchable: true,
      options: getSourcePlantOptions,
      serverExpression: 'ShippingLocation.LocationName == @val',
      valExpression: (val) => '"' + val + '"',
    },
    {
      fieldName: 'statusText',
      type: FilterType.combo,
      label: 'Status',
      options: () => ['Pending Order', 'Pending Ship', 'Completed', 'Closed', 'Canceled'],
    },
  ],
  columns: [
    {
      Header: 'Order #',
      accessor: 'orderNumber',
      type: ListViewColumnType.linkEdit,
      linkPath: ({ row }) => `/orders/edit/${row?.original?.id}`,
      excludeGlobalFilter: true,
    },
    { Header: 'Booking ID', id: 'bookingId', excludeGlobalFilter: true, accessor: ({ bookingId }) => (bookingId ? bookingId : '') },
    {
      Header: 'Customer Name',
      accessor: 'customerName',
      excludeGlobalFilter: true,
      serverExpression:
        '(OrderTypeId == 1 ? GasCustomer.CustomerName : (OrderTypeId == 2 ? LiquidCustomer.CustomerName : (OrderTypeId == 3 ? DewarCustomer.AccountName : "")))',
    },
    {
      Header: 'Port Cutoff',
      id: 'portCutoff',
      disableSortBy: true,
      excludeGlobalFilter: true,
      accessor: ({ bookingPortCutoffDateString }) => bookingPortCutoffDateString || '',
    },
    {
      Header: 'Date Requested',
      accessor: 'dateRequested',
      excludeGlobalFilter: true,
      type: ListViewColumnType.date,
      defaultSort: true,
      defaultSortDirection: 'asc',
    },
    { Header: 'City', accessor: 'city', excludeGlobalFilter: true },
    { Header: 'State', accessor: 'state', excludeGlobalFilter: true },
    { Header: 'Container #', accessor: 'containerId', excludeGlobalFilter: true, show: (_viewOptions, filtersState) => !isDewarOnlyFilter(filtersState) },
    {
      Header: 'Location',
      id: 'shippingCountry',
      excludeGlobalFilter: true,
      serverExpression: 'ShippingLocation.ResidentCountry',
      accessor: ({ shippingLocation }) => shippingLocation?.residentCountry || '',
    },
    {
      Header: 'Source Plant',
      id: 'shippingLocationName',
      excludeGlobalFilter: true,
      serverExpression: 'ShippingLocation.LocationName',
      accessor: ({ shippingLocation }) => shippingLocation?.locationName || '',
      filter: 'equals',
    },
    {
      Header: 'Delivery Type',
      id: 'deliveryType',
      excludeGlobalFilter: true,
      serverExpression: 'DeliveryType.Name',
      accessor: ({ deliveryType }) => deliveryType?.name || '',
    },
    {
      Header: 'PO #/Ord ID',
      id: 'ponumber',
      excludeGlobalFilter: true,
      accessor: ({ ponumber, orderId }) => orderId || ponumber || '',
    },
    {
      Header: 'MRI/NMR',
      id: 'isTransfill',
      disableSortBy: true,
      excludeGlobalFilter: true,
      accessor: ({ isTransfill }) => isTransfill,
    },
    {
      Header: 'P/U Only',
      id: 'isPickupOnly',
      disableSortBy: true,
      excludeGlobalFilter: true,
      accessor: ({ isPickupOnly }) => (isPickupOnly ? 'Yes' : ''),
    },
    {
      Header: 'Order Type',
      id: 'orderType',
      excludeGlobalFilter: true,
      serverExpression: 'OrderTypeId',
      accessor: ({ orderTypeId }) =>
        orderTypeId === OrderType.Gas
          ? 'Gas'
          : orderTypeId === OrderType.Liquid
          ? 'Liquid'
          : orderTypeId === OrderType.Dewar
          ? 'Dewar'
          : '',
    },
    {
      Header: 'Manifest',
      id: 'manifest',
      disableSortBy: true,
      excludeGlobalFilter: true,
      show: (_viewOptions, filtersState) => isDewarOnlyFilter(filtersState),
      accessor: ({ dewarManifestLine }) => dewarManifestLine || '',
    },
    // Hidden, search-only columns: global search limited to id fields. Numeric
    // columns use .ToString() so the backend string(object(..)) wrapper doesn't
    // blow up casting an int to a string.
    { Header: 'Order # (search)', id: 'orderNumberSearch', accessor: 'orderNumber', serverExpression: 'OrderNumber.ToString()', hidden: true },
    { Header: 'Order Id (search)', id: 'orderIdSearch', accessor: 'orderId', hidden: true },
    { Header: 'Customer # (search)', id: 'customerIdSearch', accessor: 'customerId', serverExpression: 'CustomerId.ToString()', hidden: true },
    // Customer name is globally searchable via a COALESCE (not the ternary the
    // display column uses for sort) so it survives the backend string(object(..))
    // global-search wrapper.
    { Header: 'Customer Name (search)', id: 'customerNameSearch', accessor: 'customerName', serverExpression: 'GasCustomer.CustomerName ?? LiquidCustomer.CustomerName ?? DewarCustomer.AccountName', hidden: true },
    // Hidden, filter-only columns backing the client-side Order Type / Status filters.
    { Header: 'Order Type Id', id: 'orderTypeId', accessor: 'orderTypeId', hidden: true },
    { Header: 'Status', id: 'statusText', accessor: 'statusText', filter: 'equals', hidden: true },
    /*    { Header: 'Customer', accessor: 'customerId' },
       { Header: 'Date Ordered', accessor: 'dateOrdered', type: ListViewColumnType.date },
       { Header: 'Expected Ship Date', accessor: 'expectedShipDate', type: ListViewColumnType.date },
       { Header: 'PO Number', accessor: 'ponumber' },
       { Header: 'Status', accessor: 'orderStatus' },
       { Header: 'City', accessor: 'city' },
       { Header: 'State', accessor: 'state' }, */
  ],
  /*   defaultFilter: {
      status: 'current'
    } */
} as ListView;

export const CurrentOrdersList = initListView(currentOrdersListView, placedOrdersDS);